import type { CommandPaletteGroup } from '@nuxt/ui'
import { createSharedComposable } from '@vueuse/core'

function _useHeaderLinks() {
  const route = useRoute()
  const { version } = useDocsVersion()

  const headerLinks = computed(() => {
    const to = version.value.path

    return [{
      label: '文档',
      icon: 'i-lucide-book-marked',
      to,
      search: false,
      active: route.path.startsWith(to) || route.path.startsWith(`/deploy`),
      children: [{
        label: '快速入门',
        description: '学习如何开始使用 Nuxt 构建你的第一个应用。',
        icon: 'i-lucide-rocket',
        to: `${to}/getting-started/installation`,
        active: route.path.startsWith(`${to}/getting-started`)
      }, {
        label: '结构',
        description: '了解 Nuxt 项目的目录结构。',
        icon: 'i-lucide-folder-open',
        to: `${to}/directory-structure`,
        active: route.path.startsWith(`${to}/directory-structure`)
      }, {
        label: '指南',
        description: '了解关键概念、目录结构和最佳实践。',
        icon: 'i-lucide-book-open',
        to: `${to}/guide`,
        active: route.path.startsWith(`${to}/guide`) && !route.path.startsWith(`${to}/guide/directory-structure`)
      }, {
        label: 'API',
        description: '探索 Nuxt 组件、可组合函数、工具函数等更多内容。',
        icon: 'i-lucide-code-xml',
        to: `${to}/api`,
        active: route.path.startsWith(`${to}/api`)
      }, {
        label: '部署',
        description: '将你的 Nuxt 项目部署到任何地方。',
        icon: 'i-lucide-cloud',
        to: '/deploy',
        active: route.path.startsWith('/deploy')
      }, {
        label: '示例',
        description: '发现并探索官方和社区示例。',
        icon: 'i-lucide-app-window-mac',
        to: `${to}/examples`,
        active: route.path.startsWith(`${to}/examples`)
      }, {
        label: '社区',
        description: '从社区中寻找答案和支持。',
        icon: 'i-lucide-messages-square',
        to: `${to}/community`,
        active: route.path.startsWith(`${to}/community`)
      }]
    }, {
      label: '模块',
      icon: 'i-lucide-puzzle',
      to: '/modules',
      description: '用模块增强您的 Nuxt 项目。'
    }, {
      label: '模板',
      icon: 'i-lucide-app-window',
      description: '使用 Nuxt 模板开始你的下一个项目。',
      to: '/templates'
    }, {
      label: '资源',
      icon: 'i-lucide-library',
      to: '/showcase',
      search: false,
      active: route.path.startsWith('/video-courses') || route.path.startsWith('/showcase'),
      children: [{
        label: '展示',
        description: '发现并探索使用 Nuxt 构建的项目。',
        icon: 'i-lucide-presentation',
        to: '/showcase'
      }, {
        label: '视频课程',
        description: '通过观看视频课程来学习Nuxt。',
        icon: 'i-lucide-graduation-cap',
        to: '/video-courses'
      }, {
        label: 'Nuxt认证',
        description: '获取您的资格证书。',
        icon: 'i-lucide-medal',
        to: 'https://certification.nuxt.com',
        target: '_blank'
      }]
    }, {
      label: '企业',
      icon: 'i-lucide-building-2',
      to: '/enterprise',
      search: false,
      children: [{
        label: '机构',
        to: '/enterprise/agencies',
        description: '专注于 Nuxt 开发的机构。',
        icon: 'i-lucide-handshake'
      }, {
        label: '赞助商',
        to: '/enterprise/sponsors',
        description: '帮助我们维持Nuxt的开发。',
        icon: 'i-lucide-hand-heart'
      }]
    }, {
      label: '更新',
      icon: 'i-lucide-newspaper',
      search: false,
      to: '/blog',
      children: [{
        label: '博客',
        to: '/blog',
        description: '关于 Nuxt 的新闻与更新。',
        icon: 'i-lucide-newspaper'
      }, {
        label: '更新日志',
        to: '/changelog',
        description: 'Nuxt 和官方模块的最新版本。',
        icon: 'i-lucide-history'
      }]
    }]
  })

  return { headerLinks }
}

export const useHeaderLinks = import.meta.client ? createSharedComposable(_useHeaderLinks) : _useHeaderLinks

const footerLinks = [{
  label: '社区',
  children: [{
    label: 'Nuxters',
    to: 'https://nuxters.nuxt.com',
    target: '_blank'
  }, {
    label: '团队',
    to: '/team'
  }, {
    label: '设计工具包',
    to: '/design-kit'
  }]
}, {
  label: '探索',
  children: [{
    label: '模板',
    to: '/templates'
  }, {
    label: '展示',
    to: '/showcase'
  }, {
    label: 'AI评估',
    to: '/evals'
  }]
}, {
  label: '企业',
  children: [{
    label: '机构',
    to: '/enterprise/agencies'
  }, {
    label: '赞助商',
    to: '/enterprise/sponsors'
  }]
}]

export const useFooterLinks = () => ({ footerLinks })

const _useNavigation = () => {
  const searchTerm = ref<string>('')
  const { track } = useAnalytics()
  const { open: openAgent } = useNuxtAgent()

  const { headerLinks } = useHeaderLinks()
  const { footerLinks } = useFooterLinks()
  const { modules, fetchList: fetchModules } = useModules()
  const { providers, fetchList: fetchHosting } = useHostingProviders()
  const { articles: blogArticles, fetchList: fetchBlog } = useBlog()

  const searchLinks = computed(() => [{
    label: '询问Nuxi',
    icon: 'i-custom-nuxi',
    to: 'javascript:void(0);',
    onSelect: () => {
      track('Nuxi Opened', { source: 'search-links' })
      openAgent()
    }
  }, ...headerLinks.value.flatMap((link) => {
    if (link.search === false) {
      return (link.children || []).map(child => ({
        ...child,
        label: `${link.label} > ${child.label}`
      }))
    }
    return [link]
  }), {
    label: '团队',
    icon: 'i-lucide-users',
    to: '/team'
  }, {
    label: '设计工具包',
    icon: 'i-lucide-palette',
    to: '/design-kit'
  }, {
    label: 'AI评估',
    icon: 'i-lucide-brain',
    to: '/evals'
  }, {
    label: '新闻简报',
    icon: 'i-lucide-mail',
    to: '/newsletter'
  }, {
    label: '源代码',
    icon: 'i-lucide-code',
    to: 'https://github.com/nuxt/nuxt.com',
    target: '_blank'
  }])

  const modulesItems = computed(() => modules.value.map(module => ({
    id: `module-${module.name}`,
    label: module.npm,
    suffix: module.description,
    downloads: module.stats?.downloads ?? 0,
    avatar: {
      src: moduleImage(module.icon),
      ui: {
        root: 'rounded-none bg-transparent'
      }
    },
    to: `/modules/${module.name}`
  })))

  const hostingItems = computed(() => providers.value.map(hosting => ({
    id: `hosting-${hosting.path}`,
    label: hosting.title,
    suffix: hosting.description,
    icon: hosting.logoIcon,
    avatar: hosting.logoSrc
      ? {
          src: hosting.logoSrc,
          ui: {
            root: 'rounded-none bg-transparent'
          }
        }
      : undefined,
    to: hosting.path
  })))

  const blogItems = computed(() => blogArticles.value.map(article => ({
    id: `blog-${article.path}`,
    label: article.title,
    suffix: article.description,
    icon: 'i-lucide-newspaper',
    to: article.path
  })))

  const postFilter = (searchTerm: string, items: any[]) => {
    if (!searchTerm) {
      return []
    }
    return items
  }

  const searchGroups = computed<CommandPaletteGroup[]>(() => [{
    id: 'modules-search',
    label: '模块',
    items: modulesItems.value,
    postFilter
  }, {
    id: 'hosting-search',
    label: '托管',
    items: hostingItems.value,
    postFilter
  }, {
    id: 'blog-search',
    label: '博客',
    items: blogItems.value,
    postFilter
  }, {
    id: 'ask-ai-search',
    label: 'AI',
    ignoreFilter: true,
    postFilter,
    items: [{
      label: '询问Nuxi',
      icon: 'i-custom-nuxi',
      onSelect() {
        track('Nuxi Opened', { source: 'search-palette', query: searchTerm.value })
        openAgent(searchTerm.value)
      }
    }]
  }])

  const { open: searchOpen } = useContentSearch()
  watch(searchOpen, (value) => {
    if (value) {
      fetchModules()
      fetchHosting()
      fetchBlog()
    }
  })

  watchDebounced(searchTerm, (term) => {
    if (term) {
      track('Search Performed', { term })
    }
  }, { debounce: 500 })

  return {
    searchTerm,
    headerLinks,
    footerLinks,
    searchLinks,
    searchGroups
  }
}

export const useNavigation = import.meta.client ? createSharedComposable(_useNavigation) : _useNavigation
